// material-ui
import { alpha, styled, useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// third-party
import { useDropzone } from 'react-dropzone';

// project-imports
// import RejectionFiles from './RejectionFiles';

// assets
import { Camera } from 'iconsax-react';
import useApi from 'hooks/useApi';
import { Box } from '@mui/material';
import { useState } from 'react';
import { CircularProgress } from '@mui/material';
// types
// import { CustomFile, UploadProps } from 'types/dropzone';

const RootWrapper = styled('div')(({ theme }) => ({
  width: 124,
  height: 86,
  borderRadius: '15px',
  border: '1px dashed',
  borderColor: theme.palette.primary.main,
  background: theme.palette.primary.lighter
}));

const DropzoneWrapper = styled('div')(({ theme }) => ({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '15px',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': { cursor: 'pointer', '& .placeholder': { zIndex: 9 } },
  borderColor: theme.palette.primary.main,
  background: theme.palette.primary.lighter
}));

const PlaceholderWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.primary.lighter, 0.75),
  transition: theme.transitions.create('opacity', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter
  }),
  '&:hover': { opacity: 0.85 }
}));

// ==============================|| UPLOAD - AVATAR ||============================== //

export default function AvatarDropzone({ error, path, setFieldValue, sx, url, validation, setTouched, setErrors, validateField }: any) {
  const theme = useTheme();
  const { uploadAsset } = useApi();
  const [isUploading, setUploading] = useState(false);
  const { width, height, ratio, fileSizeInMb }: { width?: number; ratio?: number; height?: number; fileSizeInMb?: number } = validation;

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => resolve(Promise.resolve({ width: img.width, height: img.height }));
        img.onerror = reject;
        img.src = event.target?.result as string;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const isValidImageRatio = (width: number, height: number, targetRatio: number) => {
    const calculatedRatio = width / height;
    return Math.abs(calculatedRatio - targetRatio) < 0.01;
  };

  const isValidImageWidth = (width: number, maxWidth: number) => {
    return width <= maxWidth;
  };

  const isValidImageHeight = (height: number, maxHeight: number) => {
    return height <= maxHeight;
  };

  const isValidFileSize = (file: File, maxSizeMB = 1) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to Bytes
    return file.size <= maxSizeBytes;
  };

  const throwError = (errorMessage: string) => {
    console.log(`theme.icons.${path}.url`, '`theme.icons.${path}.url`');
    setErrors(`theme.icons.${path}.url`, errorMessage);
    setTouched(`theme.icons.${path}.url`, true);
    validateField(`theme.icons.${path}.url`);
  };

  const uploadClientIcon = async (file: File, filePath: string) => {
    setUploading(true);
    try {
      let dimentions = await getImageDimensions(file);

      if (fileSizeInMb) {
        let isSizeOk = isValidFileSize(file, fileSizeInMb);
        if (!isSizeOk) {
          throwError(`File should be less than ${fileSizeInMb}MB.`);
          setUploading(false);
          return;
        }
      }

      if (height) {
        let isHeightOk = isValidImageHeight(dimentions.height, height);
        if (!isHeightOk) {
          throwError(`Max height should be ${height}px.`);
          setUploading(false);
          return;
        }
      }

      if (width) {
        let isWidthOk = isValidImageWidth(dimentions.width, width);
        if (!isWidthOk) {
          throwError(`Max width should be ${width}px.`);
          setUploading(false);
          return;
        }
      }

      if (ratio) {
        let isValid = isValidImageRatio(dimentions.width, dimentions.height, ratio);
        if (!isValid) {
          throwError(`Invalid Image Ratio.`);
          setUploading(false);
          return;
        }
      }

      const response = await uploadAsset(file, filePath);
      const { success, message, data } = response;
      if (success) {
        setFieldValue(`theme.icons.${path}`, data);
      } else {
        throwError(message);
      }

      setUploading(false);
    } catch (error) {
      setUploading(false);
      throwError(`Unable to upload image.`);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: { 'image/png': ['.png'], 'image/svg+xml': ['.svg'] },
    multiple: false,
    onDrop: (acceptedFiles: any[]) => {
      if (acceptedFiles.length > 0) {
        setUploading(true);
        let file = acceptedFiles[0];
        let filePath = 'settings/icons/clients';
        uploadClientIcon(file, filePath);
      }
    }
  });

  const thumbs = url && (
    <Box
      sx={{
        width: 124,
        height: 86,
        background: `url(${url})`,
        backgroundPosition: 'center',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 1
      }}
    />
  );
  const loading = isUploading && (
    <Box
      sx={{
        width: 124,
        height: 86,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 1
      }}
    >
      <CircularProgress color="primary" size="sm" />
    </Box>
  );

  return (
    <>
      <RootWrapper sx={{ ...((isDragReject || error) && { borderColor: 'error.light' }), ...sx }}>
        <DropzoneWrapper {...getRootProps()} sx={{ ...(isDragActive && { opacity: 0.6 }) }}>
          <input {...getInputProps()} />
          {thumbs}
          {loading}
          <PlaceholderWrapper
            className="placeholder"
            sx={{
              ...(thumbs && { opacity: 0, color: 'common.white', bgcolor: 'secondary.darker' }),
              ...((isDragReject || error) && { bgcolor: 'error.lighter' })
            }}
          >
            <Stack spacing={0.5} alignItems="center">
              <Camera style={{ color: theme.palette.secondary.main, fontSize: '2rem' }} />
              <Typography color="secondary">{url ? 'Update' : 'Upload'}</Typography>
            </Stack>
          </PlaceholderWrapper>
        </DropzoneWrapper>
      </RootWrapper>
      {/* {fileRejections.length > 0 && <RejectionFiles fileRejections={fileRejections} />} */}
    </>
  );
}
